import React, { useState } from "react";
import DynamicForm from "../Components/DynamicForm";
import { useUser } from "../UserContext";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SlidingSidebar, {
  ButtonControls,
  SidebarEditForm,
} from "../Components/SlidingSidebar";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// Define constants for user fields
const PERSONAL_FIELDS = [
  "first_name",
  "last_name",
  "email",
  "password",
  "address",
  "city",
  "state_code",
  "zip",
];

// Add interface for form field types
interface FormField {
  label: string;
  value:
    | string
    | number
    | Array<{ label: string; value: string | number }>
    | null;
  field: string;
  type?: "text" | "select" | "autocomplete" | "multiselect" | "password";
  options?: Array<{ label: string; value: string | number }>;
  disabled?: boolean;
  required?: boolean;
  onChange?: (value: any) => void;
  renderRight?: React.ReactNode;
}

const UserInfo: React.FC = () => {
  const { currentUser, handleUserSelect } = useUser();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editData, setEditData] = useState<any>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handlePersonalUpdate = async (fieldName: string, value: string) => {
    if (!currentUser?.user_id) {
      console.error("Current user is not defined or user_id is missing.");
      return;
    }

    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/users/user-profile/${
          currentUser.user_id
        }`,
        { fieldName, value: value ?? null }
      );

      // Call handleUserSelect to refresh all user data
      if (currentUser) {
        await handleUserSelect(currentUser.user_id);
      }

      setSnackbarMessage("User information updated successfully.");
    } catch (error) {
      console.error("Error updating user data", error);
      setSnackbarMessage("Failed to update user information.");
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters long");
      return;
    }

    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/users/change-password/${
          currentUser?.user_id
        }`,
        {
          currentPassword,
          newPassword,
        }
      );

      setSnackbarMessage("Password updated successfully");
      setPasswordDialogOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordError("");
    } catch (error) {
      console.error("Error changing password", error);
      setPasswordError(
        "Failed to change password. Please check your current password."
      );
    } finally {
      setSnackbarOpen(true);
    }
  };

  const getPersonalFields = () => {
    return PERSONAL_FIELDS.map((key) => ({
      name: key,
      label:
        key === "state_code"
          ? "State"
          : key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase()),
      fieldType: key === "password" ? "password" : "text",
      value: key === "password" ? "********" : "",
      editable: key !== "password",
    }));
  };

  const getPersonalFormSections = () => {
    return [
      {
        title: "Personal Information",
        fields: [
          ...PERSONAL_FIELDS.map(
            (field) =>
              ({
                label:
                  field === "state_code"
                    ? "State"
                    : field
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase()),
                value:
                  field === "password" ? "********" : editData?.[field] || "",
                field: field,
                type: field === "password" ? "password" : "text",
                required: field !== "password",
                disabled: field === "password",
                onChange:
                  field === "password"
                    ? () => setPasswordDialogOpen(true)
                    : undefined,
                renderRight:
                  field === "password" ? (
                    <Button
                      onClick={() => setPasswordDialogOpen(true)}
                      variant="outlined"
                      sx={{
                        height: "37px", // Match input height
                        minWidth: "auto",
                        whiteSpace: "nowrap",
                        ml: 2,
                        transform: "translateY(-8px)", // Fine-tune vertical alignment
                      }}
                    >
                      Change Password
                    </Button>
                  ) : undefined,
              } as FormField)
          ),
        ],
      },
    ];
  };

  const handleSavePersonal = async () => {
    try {
      for (const field of PERSONAL_FIELDS) {
        await handlePersonalUpdate(field, editData[field]);
      }
      setEditMode(false);
      setSnackbarMessage("Personal information updated successfully");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error saving personal data:", error);
      setSnackbarMessage("Failed to update personal information");
      setSnackbarOpen(true);
    }
  };

  const handleEditPersonal = () => {
    setEditMode(true);
    setEditData({ ...currentUser }); // Set initial data when entering edit mode
  };

  return (
    <>
      <DynamicForm
        fields={getPersonalFields()}
        initialData={{
          ...currentUser,
          password: "********",
        }}
        onEdit={handleEditPersonal}
        section={{
          label: "Personal",
          tableName: "user_data",
        }}
      />

      <SlidingSidebar
        open={editMode}
        onClose={() => setEditMode(false)}
        width="500px"
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flexGrow: 1, overflow: "auto", pb: 2 }}>
            <SidebarEditForm
              sections={getPersonalFormSections()}
              onValueChange={(field: string, value: any) => {
                setEditData((prev: Record<string, any>) => ({
                  ...prev,
                  [field]: value,
                }));
              }}
            />
          </Box>

          <ButtonControls>
            <Button
              onClick={handleSavePersonal}
              variant="contained"
              color="primary"
            >
              Save Changes
            </Button>
            <Button onClick={() => setEditMode(false)} variant="outlined">
              Cancel
            </Button>
          </ButtonControls>
        </Box>
      </SlidingSidebar>

      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Current Password"
            type={showCurrentPassword ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle current password visibility"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                    size="small"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle new password visibility"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    size="small"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {passwordError && (
            <Typography color="error" sx={{ mt: 1 }}>
              {passwordError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            color="primary"
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default UserInfo;
